import isNil from 'lodash/isNil';
import { BoxProps, IconProps, TextProps, TypeIconName } from '@bitrise/bitkit';
import { PublicApiApp } from '@/models/App';
import { LogFormat } from '@/models/LogFormat';
import { Runnable } from '@/models/Runnable';
import { TriggerParams } from '@/models/TriggerParams';
import BuildStatus from './BuildStatus';
import ArtifactTypeFilter from './ArtifactTypeFilter';
import { BuildCacheInfo } from './BuildCacheInfo';
import { BuildToolInvocation } from './BuildToolInvocation';

export type InstallableArtifact = {
  artifactType: ArtifactTypeFilter.APK | ArtifactTypeFilter.IPA;
  id: string;
  title: string;
  versionCode?: string;
  versionName?: string;
};

export type CommonBuild<StartedAtKey extends 'startedAt' | 'startedOnWorkerAt'> = Runnable<
  BuildStatus,
  StartedAtKey
> & {
  artifacts?: InstallableArtifact[];
  branch: string;
  buildNumber: number;
  commitHash?: string;
  commitMessage?: string;
  isOnHold: boolean;
  isProcessed: boolean;
  isPipeline?: boolean;
  pipelineWorkflowId?: string | null;
  pullRequestId?: number;
  pullRequestTargetBranch?: string;
  repository?: PublicApiApp;
  rebuildable: boolean;
  slug: string;
  tag?: string;
  triggerParams?: TriggerParams;
  originalBuildParams?: TriggerParams;
  triggeredWorkflow: string | null;
  buildCacheInfo: BuildCacheInfo;
  buildToolInvocations?: BuildToolInvocation[];
};

export type Build = CommonBuild<'startedAt'>;

export type PublicApiBuild = CommonBuild<'startedOnWorkerAt'> & {
  commitViewUrl?: string;
  logFormat?: LogFormat;
  originalBuildParams: TriggerParams;
  pullRequestViewUrl?: string;
  statusText?: string;
  pipelineWorkflowId: string | null;
  triggeredWorkflow: string;
};

export type RemoteAccessData = {
  screenshare?: {
    address: string;
    password: string;
    username: string;
  };
  ssh: {
    command: string;
    host: string;
    port: string;
    user: string;
    password: string;
  };
};

export const isPullRequest = (build: CommonBuild<'startedAt' | 'startedOnWorkerAt'>): boolean =>
  !!build?.pullRequestTargetBranch;

export const sourceBranch = (build: CommonBuild<'startedAt' | 'startedOnWorkerAt'>): string | undefined =>
  isPullRequest(build) ? build?.branch : undefined;

export const targetBranch = (build: CommonBuild<'startedAt' | 'startedOnWorkerAt'>): string | undefined =>
  build.pullRequestTargetBranch || build.branch;

export const isBuildStarting = (build: CommonBuild<'startedAt' | 'startedOnWorkerAt'>): boolean =>
  build.status === BuildStatus.Running && isNil(build.startedOnWorkerAt) && isNil(build.startedAt);

export const getBuildStatusIconName = (build: CommonBuild<'startedAt' | 'startedOnWorkerAt'>): TypeIconName => {
  switch (build.status) {
    case BuildStatus.Running:
      return build.isOnHold ? 'Time' : 'SpinnerPurpleDouble';
    case BuildStatus.Success:
      return 'CheckCircle';
    case BuildStatus.Error:
      return 'CrossCircleFilled';
    case BuildStatus.Aborted:
    case BuildStatus.AbortedWithSuccess:
      return 'AbortCircleFilled';
    default:
      return 'Circle';
  }
};

export const getBuildStatusText = (build: CommonBuild<'startedAt' | 'startedOnWorkerAt'>): string => {
  if (build.isOnHold) {
    return 'On hold';
  }
  switch (build.status) {
    case BuildStatus.Running: {
      return isBuildStarting(build) ? 'Starting' : 'Running';
    }
    case BuildStatus.Success:
      return 'Success';
    case BuildStatus.Error:
      return 'Failed';
    case BuildStatus.Aborted:
    case BuildStatus.AbortedWithSuccess:
      return 'Aborted';
    default:
      throw new Error(`Unsupported build status: ${build.status}`);
  }
};

export const getTriggeredByText = (build: PublicApiBuild): string | undefined => {
  if (!build.triggeredBy) {
    return undefined;
  }

  if (build.triggeredBy === 'schedule') {
    return 'Schedule';
  }
  if (build.triggeredBy === 'webhook') {
    return 'Webhook';
  }
  if (build.triggeredBy === 'curl') {
    return 'cURL';
  }

  const manualPrefix = 'manual-';

  if (build.triggeredBy.startsWith(manualPrefix)) {
    const username = build.triggeredBy.replace(manualPrefix, '');
    return `Manual - @${username}`;
  }

  const webhookPrefix = 'webhook-';

  if (build.triggeredBy.startsWith(webhookPrefix)) {
    const providerAndUserName = build.triggeredBy.replace(webhookPrefix, '');
    return `Webhook - @${providerAndUserName}`;
  }

  return build.triggeredBy;
};

export enum TriggerTypes {
  PUSH = 'push',
  PULL_REQUEST = 'pull-request',
  TAG = 'tag',
}

export const getTriggerType = ({
  tag,
  pullRequestId,
}: {
  tag?: string;
  pullRequestId?: number | string;
}): TriggerTypes => {
  if (pullRequestId) {
    return TriggerTypes.PULL_REQUEST;
  }
  if (tag) {
    return TriggerTypes.TAG;
  }
  return TriggerTypes.PUSH;
};

export const getTriggerTypeIcon = ({
  tag,
  pullRequestId,
}: {
  tag?: string;
  pullRequestId?: number | string;
}): TypeIconName => {
  const triggerType = getTriggerType({ tag, pullRequestId });
  switch (triggerType) {
    case TriggerTypes.PULL_REQUEST:
      return 'Pull';
    case TriggerTypes.TAG:
      return 'Tag';
    case TriggerTypes.PUSH:
    default:
      return 'Push';
  }
};

export const isBuildOnHold = (build: CommonBuild<'startedAt' | 'startedOnWorkerAt'>): boolean =>
  build && build.status === BuildStatus.Running && build.isProcessed && build.isOnHold;

export const isBuildRunning = (build: CommonBuild<'startedAt' | 'startedOnWorkerAt'>): boolean =>
  build && build.status === BuildStatus.Running && build.isProcessed && !build.isOnHold;

export const isBuildAborted = (build: CommonBuild<'startedAt' | 'startedOnWorkerAt'>): boolean =>
  build && (build.status === BuildStatus.Aborted || build.status === BuildStatus.AbortedWithSuccess);

export const isBuildFinished = (build: CommonBuild<'startedAt' | 'startedOnWorkerAt'>): boolean =>
  build && build.finishedAt !== null;

export const getBuildStatusColors = (
  build: CommonBuild<'startedAt' | 'startedOnWorkerAt'>,
): {
  color: IconProps['color'];
  textColor: TextProps['color'];
  backgroundColor: BoxProps['color'];
  hoverBackgroundColor: BoxProps['color'];
} => {
  switch (build.status) {
    case BuildStatus.Running:
      if (build && isBuildOnHold(build)) {
        return {
          backgroundColor: 'status/neutral/bg',
          textColor: 'status/neutral/text',
          color: 'sys/neutral/base',
          hoverBackgroundColor: 'sys/neutral/subtle',
        };
      }

      if (build && isBuildStarting(build)) {
        return {
          backgroundColor: 'status/info/bg',
          textColor: 'status/info/text',
          color: 'sys/info/base',
          hoverBackgroundColor: 'sys/info/subtle',
        };
      }

      return {
        backgroundColor: 'status/progress/bg',
        textColor: 'status/progress/text',
        color: 'sys/interactive/base',
        hoverBackgroundColor: 'sys/interactive/subtle',
      };
    case BuildStatus.Success:
      return {
        backgroundColor: 'status/success/bg',
        textColor: 'status/success/text',
        color: 'sys/success/base',
        hoverBackgroundColor: 'sys/success/subtle',
      };
    case BuildStatus.Error:
      return {
        backgroundColor: 'status/critical/bg',
        textColor: 'status/critical/text',
        color: 'sys/critical/base',
        hoverBackgroundColor: 'sys/critical/subtle',
      };
    case BuildStatus.Aborted:
    case BuildStatus.AbortedWithSuccess:
      return {
        backgroundColor: 'status/warning/bg',
        textColor: 'status/warning/text',
        color: 'sys/warning/bold',
        hoverBackgroundColor: 'sys/warning/subtle',
      };
    default:
      return {
        backgroundColor: 'status/neutral/bg',
        textColor: 'status/neutral/text',
        color: 'sys/neutral/base',
        hoverBackgroundColor: 'sys/bg/subtle',
      };
  }
};

export const isBuildStarted = (build: PublicApiBuild) => build.startedOnWorkerAt !== null;

export const isBuildNotFinished = (build: PublicApiBuild): boolean =>
  isBuildStarting(build) || isBuildOnHold(build) || isBuildRunning(build);

export const buildStateSelector = (build: PublicApiBuild) => {
  return {
    hasStarted: isBuildStarted(build),
    hasNotFinished: isBuildNotFinished(build),
    hasFinished: isBuildFinished(build),
  };
};

const TenMinutesInMilliseconds = 6e5;
export const isRecentlyFinished = (build: PublicApiBuild): boolean => {
  if (!build.finishedAt) {
    return false;
  }

  const durationMilliseconds = Date.parse(build.finishedAt) - Date.now();
  return durationMilliseconds < TenMinutesInMilliseconds;
};

export const calculateDuration = (build: CommonBuild<'startedAt' | 'startedOnWorkerAt'>): number => {
  const startedAtString = build.startedAt ?? build.startedOnWorkerAt;
  if (!startedAtString) {
    return 0;
  }

  const startedAt = new Date(startedAtString);
  const finishedAt = build.finishedAt ? new Date(build.finishedAt) : new Date();

  return Math.round((finishedAt.getTime() - startedAt.getTime()) / 1000);
};

export const calculateProgressPercentage = (
  buildDuration: number,
  referenceBuildDuration: number,
  percentageCapIfRunning = 98,
): number => {
  if (referenceBuildDuration === 0) {
    return 0;
  }
  let percentage = (100 * buildDuration) / referenceBuildDuration;
  if (percentageCapIfRunning) {
    percentage = Math.min(percentageCapIfRunning, percentage);
  }

  return percentage;
};

export const getBuildToolInvocation = (build: Build): BuildToolInvocation | null | undefined => {
  if (!build.buildToolInvocations) {
    return undefined;
  }
  if (build.buildToolInvocations.length === 0) {
    return null;
  }
  let buildToolInvocation = build.buildToolInvocations.find((b) => b.toolVersion);
  if (!buildToolInvocation) {
    [buildToolInvocation] = build.buildToolInvocations;
  }
  return buildToolInvocation;
};
